import { Injectable } from "@angular/core";
import { Observable } from "rxjs/Observable";
import { Account, AccountDetails, AccountDTO } from "../models/account";
import { environment } from "../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { AccountFilter } from "../models/filters/account-filter";
import { Roles } from "../models/roles";
import {
  checkIfTruncated,
  getFileNameFromResponseContentDisposition,
  saveFile,
} from "./utils/file-download-helper";

@Injectable()
export class AccountService {
  USER_API_URL: string = environment.serverUrl + "api/1/users";

  public roles: Roles[] = [];
  public supportedLocales: string[];
  public locale: string = "FR";

  constructor(private httpClient: HttpClient) {}

  /**
   * Provide further information about an account
   * @param account
   */
  getAccountDetails(account: Account): Observable<AccountDetails> {
    const url = `${this.USER_API_URL}/${account.email}`;
    return this.httpClient.get<AccountDetails>(url);
  }

  getPaginatedFilteredSortedAccountsList(
    pageIndex: number,
    itemPerPage: number,
    filters: AccountFilter,
    criterias: Map<string, boolean>,
    groupId: number
  ): any {
    const url = this.getFormattedURL(
      pageIndex,
      itemPerPage,
      criterias,
      filters,
      groupId
    );
    return this.httpClient
      .get<any>(encodeURI(url).replace("+", "%2b"), { observe: "response" })
      .map((response) => {
        return {
          headers: response.headers,
          res: <AccountDTO[]>response.body,
        };
      });
  }

  create(account: AccountDetails): Observable<AccountDetails> {
    return this.httpClient.post<AccountDetails>(
      this.USER_API_URL + "?temporaryPassword=" + account.temporaryPassword,
      account
    );
  }

  update(account: AccountDetails): Observable<AccountDetails> {
    return this.httpClient.put<AccountDetails>(
      `${this.USER_API_URL}/${account.email}`,
      account
    );
  }

  delete(account: Account): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.USER_API_URL}/${account.email}`
    );
  }

  isUserExist(email: string): Observable<boolean> {
    return this.httpClient.get<boolean>(
      encodeURI(this.USER_API_URL + "/exists/" + email)
    );
  }

  canUpdateUser(email: string): Observable<boolean> {
    return this.httpClient.get<boolean>(
      encodeURI(this.USER_API_URL + "/canUpdate/" + email)
    );
  }

  updateLocale(newLocale: string) {
    let a: AccountDetails = new AccountDetails();
    a.language = newLocale;
    return this.httpClient.patch(`${this.USER_API_URL}`, a);
  }

  getAccountByGroupAndType(groupId: number, contractType: Roles): any {
    const url =
      this.USER_API_URL +
      "?pagination=0-100&groupId=" +
      groupId +
      "&contractType=" +
      contractType;
    return this.httpClient
      .get(encodeURI(url), { observe: "response" })
      .map((response) => {
        return {
          headers: response.headers,
          res: <AccountDTO[]>response.body,
        };
      });
  }

  downloadXLSFile(
    pageIndex: number,
    itemPerPage: number,
    filters: AccountFilter,
    criterias: Map<string, boolean>,
    groupId: number
  ): any {
    const url = this.getFormattedURL(
      pageIndex,
      itemPerPage,
      criterias,
      filters,
      groupId
    );
    return this.httpClient
      .get<any>(encodeURI(url), {
        headers: { Accept: "application/octet-stream" },
        responseType: "blob" as "json",
        observe: "response",
      })
      .map((response) => {
        const fileName = getFileNameFromResponseContentDisposition(
          response,
          "account.xls"
        );
        const isTruncated = checkIfTruncated(response);
        saveFile(response.body.slice(), fileName);
        return isTruncated;
      });
  }

  private getFormattedURL(
    pageIndex: number,
    itemPerPage: number,
    criterias: Map<string, boolean>,
    filters: AccountFilter,
    groupId: number
  ) {
    const offset = (pageIndex - 1) * itemPerPage; // records offset to display the specific range

    let url =
      this.USER_API_URL +
      "?pagination=" +
      offset +
      "-" +
      itemPerPage +
      "&sorts=";

    if (criterias.size === 0) {
      // if no sorts criterias => sort by lastName
      url += "user.lastName";
    } else {
      Array.from(criterias.keys()).forEach(
        (c) => (url += c + (criterias.get(c) === true ? " asc" : " desc") + ",")
      );
      url = url.substring(0, url.length - 1);
    }

    if (filters !== undefined) {
      const filtersAsString = JSON.stringify(filters, (key, value) => {
        if (value !== null && value !== undefined && value !== "") {
          return value;
        }
      });
      if (filtersAsString.length > 2) {
        url += "&filters=" + filtersAsString;
      }
    }
    if (groupId && groupId != 0) {
      url += "&groupId=" + groupId;
    }
    return url;
  }

  /**
   * Return a list of all the supported locales
   */
  getSupportedLocales(): Promise<string[]> {
    const options = { responseType: "text" as "text" }; // workaround because we don't receive well formed JSON
    return this.httpClient
      .get(encodeURI(this.USER_API_URL + "/locales"), options)
      .toPromise()
      .then((res) => {
        if (res) {
          let languages: string[] = res.split(",");
          for (var i = 0; i < languages.length; i++) {
            languages[i] = languages[i].trim();
          }
          this.supportedLocales = languages;
          return this.supportedLocales;
        }
      });
  }

  getLocale(): Promise<string> {
    const options = { responseType: "text" as "text" }; // workaround because we don't receive well formed JSON
    return this.httpClient
      .get(encodeURI(this.USER_API_URL + "/locale"), options)
      .toPromise()
      .then((res) => {
        if (res) {
          this.locale = res;
          return this.locale;
        }
      });
  }

  getRoles(): Promise<any> {
    return this.httpClient
      .get<Roles[]>(encodeURI(this.USER_API_URL + "/roles"))
      .toPromise()
      .then((res) => {
        if (res) {
          this.roles = res;
          return res;
        }
      });
  }

  resetPassword(mail: string): any {
    let url = encodeURI(this.USER_API_URL + "/passwordUpdate/" + mail);
    return this.httpClient.get(url);
  }

  isSuperAdmin(): boolean {
    return this.roles.length > 0 && this.roles.includes(Roles.SUPERADMIN);
  }

  isAdmin(): boolean {
    return this.roles.includes(Roles.ADMIN) || this.isSuperAdmin();
  }

  isMaintainer() {
    return this.roles.includes(Roles.MAINTAINER) || this.isAdmin();
  }
}



// WEBPACK FOOTER //
// ./src/app/services/account.service.ts